import { FC, PropsWithChildren, useEffect } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { CalloutBanner } from '../../../common/components/promo-banners';
import { CALL_INTEL_CSV_EXPORT_BANNER_FF, CSV_EXPORT_HELP_LINK } from '../../constants';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

interface CsvExportCalloutWrapperProps {
  locationIds?: string[];
  isDemo?: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  innerRef?: React.Ref<HTMLDivElement>;
  onDismiss?: () => void;
}

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-intel-csv-export-banner';

export const CsvExportCalloutWrapper: FC<PropsWithChildren<CsvExportCalloutWrapperProps>> = ({
  locationIds = [],
  isDemo,
  anchorRef,
  innerRef,
  onDismiss,
}) => {
  const { t } = useTranslation('highValueAdoption');

  const { getDialogProps, open, close, refs } = usePopoverDialog({
    placement: 'left',
  });

  const { handleDismiss, shouldShowBanner, ref } = useCallIntelInAppBannerVisibility({
    bannerFF: CALL_INTEL_CSV_EXPORT_BANNER_FF,
    isDemo,
    locationIds,
    componentId: COMPONENT_ID,
    enabled: true,
    maxVisibilityCount: Infinity,
    maxInteractionCount: 0,
    showOncePerDay: true,
    onDismiss,
  });

  useEffect(() => {
    if (anchorRef.current) {
      refs.setReference(anchorRef.current);
    }
  }, [anchorRef, refs]);

  useEffect(() => {
    if (shouldShowBanner) open();
  }, [shouldShowBanner, open]);

  const onGotIt = () => {
    handleDismiss();
    close();
  };

  const onLearnMore = () => {
    handleDismiss();
    close();
    window.open(CSV_EXPORT_HELP_LINK, '_blank', 'noopener,noreferrer');
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <PopoverDialog {...getDialogProps()} css={styles.dialog}>
      <CalloutBanner
        arrowPosition={'right'}
        badge='new'
        innerRef={composeRefs(innerRef, ref) as React.Ref<HTMLDivElement>}
        title={
          <Text size='medium' weight='bold'>
            {t('Do More With Your Data')}
          </Text>
        }
        description={
          <Text size='medium'>
            {t(
              'Download a CSV file of your call data to build custom reports, track trends, and share performance insights with your team.'
            )}
          </Text>
        }
        actions={
          <>
            <Button
              variant='tertiary'
              size='small'
              trackingId={getInAppAwarenessBannerTrackingId({
                source: COMPONENT_ID,
                action: 'learn-more',
              })}
              onClick={onLearnMore}
              css={styles.actionBtn}
            >
              {t('Learn more')}
            </Button>
            <Button
              variant='tertiary'
              size='small'
              trackingId={getInAppAwarenessBannerTrackingId({
                source: COMPONENT_ID,
                action: 'dismiss',
              })}
              onClick={onGotIt}
            >
              {t('Got it')}
            </Button>
          </>
        }
      />
    </PopoverDialog>
  );
};

const styles = {
  dialog: css`
    width: 336px;
    padding: 0;
    box-shadow: none;
  `,
  actionBtn: css`
    color: ${theme.font.colors.subdued};
    font-weight: ${theme.font.weight.regular};
  `,
};
